import React, {useContext, useMemo} from 'react';
import 'flag-icons/css/flag-icons.min.css';
import {AppCtx} from "../../../../AppCtx";

const FileLine = ({line, songCurrentPosition, areChordsVisible, isLyricSeparatorEnabled, isLyricsOnlyMode}) => {
  const isMeta = (line) => {
    return line.type === 'text' && line.content.match(/^\s*\[(((Title|Tag|Artist|Key|DisplayKey|Doc|Notation|Url):)?(.*))\]\s*$/i);
  };

  const isEmpty = (line) => {
    return line.type === 'text' && line.content.trim() === '';
  };

  const isChord = (line) => {
    return line.type === 'chord';
  };

  const isText = (line) => {
    return line.type === 'text';
  };
  
  const isInlineLyrics = (line) => {
    return line.type === 'inline-lyrics';
  };

  const meta = isMeta(line);
  const lineEmpty = isEmpty(line);

  // Handle inline lyrics (chord segments bound to text)
  if (isInlineLyrics(line)) {
    const hasNonWhitespaceContent = line.segments && line.segments.some(s => !s.isWhitespace);
    
    return (
      <div 
        id={`line-s${songCurrentPosition}-l${line.lineNumber}`} 
        className={`InlineLyricsLine ${!hasNonWhitespaceContent && isLyricSeparatorEnabled ? 'no-break' : ''}`}
      >
        {line.segments && line.segments.map((segment, i) => {
          // For all whitespace segments, replace spaces with non-breaking spaces to preserve spacing
          const displayText = segment.isWhitespace 
            ? segment.text.replace(/ /g, '\u00A0')
            : segment.text;
          
          return (
            <span 
              key={i} 
              className={`InlineChordSegment ${segment.chord ? 'has-chord' : ''} ${segment.isWhitespace ? 'whitespace-only' : ''}`}
            >
              {segment.chord && areChordsVisible && !isLyricsOnlyMode && (
                <span className="InlineChord">{segment.chord}</span>
              )}
              <span className="InlineText">{displayText}</span>
            </span>
          );
        })}
        {hasNonWhitespaceContent && isLyricSeparatorEnabled &&
          <span style={{color:'var(--enter-symbol-color)',fontSize:"0.8em",fontWeight:"bold"}}>↵ </span>}
      </div>
    );
  }

  if (isChord(line) && areChordsVisible && !isLyricsOnlyMode) {
    return (
      <div id={`line-s${songCurrentPosition}-l${line.lineNumber}`} className="ChordLine">
        {line.content.map((chord, index) => (
          <React.Fragment key={index}>
            <span className="Prefix">{chord.prefix}</span>
            <span className="Chord">{chord.chord}</span>
            <span className="Suffix">{chord.suffix}</span>
          </React.Fragment>
        ))}
      </div>
    );
  } else if (meta) {
    return null;
  } else if (isText(line)) {
    const content = !areChordsVisible && typeof line.content === 'string' ? line.content.trim().replace(/\s+/g, ' ') : line.content;
    return (
      <div className={`LyricLine ${!areChordsVisible ? 'extra-spacing bold-text' : ''} ${!lineEmpty && isLyricSeparatorEnabled ? 'no-break' : ''}`} id={`line-s${songCurrentPosition}-l${line.lineNumber}`}>
        {content}
        {!lineEmpty && isLyricSeparatorEnabled && <span style={{color: 'var(--enter-symbol-color)', fontSize:"0.8em", fontWeight:"bold"}}>↵ </span>}
      </div>
    );
  } else {
    return null;
  }
};

const GroupOfLines = ({group, songCurrentPosition, areChordsVisible, isLyricSeparatorEnabled, isLyricsOnlyMode}) => {
  const isMeta = (line) => {
    return line.type === 'text' && line.content.match(/^\s*\[(((Title|Tag|Artist|Key|DisplayKey|Doc|Notation):)?(.*))\]\s*$/i);
  };

  const isEmpty = (line) => {
    return line.type === 'text' && line.content.trim() === '';
  };

  const isMetaHandled = (line) => {
    const meta = isMeta(line);
    return meta && meta[3] && ['title', 'tag', 'artist', 'doc', 'key', 'displaykey', 'notation'].includes(meta[3].toLowerCase());
  };

  const filteredGroup = group.filter(line => !isMetaHandled(line));

  const onlyMeta = filteredGroup.every(line => isMeta(line) || isEmpty(line));

  return (
    <div className={`cords-and-text ${onlyMeta ? 'Meta' : 'Lyrics'} ${isLyricSeparatorEnabled && !onlyMeta ? 'no-break' : ''}`}
         id={`group-s${songCurrentPosition}-l${group[0].lineNumber}`}>
      {filteredGroup.map((line, index) => (
        <FileLine
          key={index}
          line={line}
          songCurrentPosition={songCurrentPosition}
          areChordsVisible={areChordsVisible}
          isLyricSeparatorEnabled={isLyricSeparatorEnabled}
          isLyricsOnlyMode={isLyricsOnlyMode}
        />
      ))}
    </div>
  );
};

const SectionElements = ({section, songCurrentPosition, areChordsVisible, isLyricSeparatorEnabled, isLyricsOnlyMode}) => {
  // Safety check: ensure section exists and has content array
  if (!section || !section.content || !Array.isArray(section.content) || section.content.length === 0) {
    return null;
  }

  // Safety check: ensure first group has at least one line for ID generation
  const firstLineNumber = section.content[0] && section.content[0][0] 
    ? section.content[0][0].lineNumber 
    : 0;

  return (
    <div id={`section-s${songCurrentPosition}-l${firstLineNumber}`}
         className={`section section-${section.typeOfVerse}`}>
      {section.content.map((group, index) => (
        <GroupOfLines
          key={index}
          group={group}
          songCurrentPosition={songCurrentPosition}
          areChordsVisible={areChordsVisible}
          isLyricSeparatorEnabled={isLyricSeparatorEnabled}
          isLyricsOnlyMode={isLyricsOnlyMode}
        />
      ))}
    </div>
  );
};

const PresentationSongComponent = ({ song, chordsVisible, isLyricsOnlyMode }) => {
  const {areChordsVisible: contextChordsVisible, isLyricSeparatorEnabled} = useContext(AppCtx);
  const areChordsVisible = typeof chordsVisible === 'boolean' ? chordsVisible : contextChordsVisible;
  const hasValidSections = Array.isArray(song?.sections) && song.sections.length > 0;

  const lyricsOnlyText = useMemo(() => {
    if (!isLyricsOnlyMode || !hasValidSections) {
      return "";
    }

    const normalize = (text) => (typeof text === "string" ? text : "").replace(/\s+/g, " ").trim();
    const isMetaLine = (line) => {
      if (!line || line.type !== "text" || typeof line.content !== "string") {
        return false;
      }
      return /^\s*\[(((Title|Tag|Artist|Key|DisplayKey|Doc|Notation|Url):)?(.*))]\s*$/i.test(line.content);
    };

    const extractFromLine = (line) => {
      if (!line || isMetaLine(line)) {
        return "";
      }

      if (line.type === "inline-lyrics") {
        const segments = line.segments || [];
        const raw = segments.map((segment) => segment?.text ?? "").join("");
        return normalize(raw);
      }

      if (line.type === "text") {
        if (typeof line.content === "string") {
          return normalize(line.content);
        }
        if (Array.isArray(line.content)) {
          return normalize(line.content.join(""));
        }
      }

      if (line.type === "comment" && typeof line.content === "string") {
        return normalize(line.content);
      }

      if (typeof line.content === "string") {
        return normalize(line.content);
      }

      return "";
    };

    const parts = [];

    song.sections.forEach((section) => {
      section?.content?.forEach((group) => {
        group?.forEach((line) => {
          if (!line || line.type === "chord") {
            return;
          }
          const text = extractFromLine(line);
          if (text) {
            parts.push(text);
          }
        });
      });
    });

    return parts.join(" ").replace(/\s+/g, " ").trim();
  }, [isLyricsOnlyMode, hasValidSections, song]);

  if (!hasValidSections) {
    return null;
  }

  if (isLyricsOnlyMode && lyricsOnlyText) {
    return (
      <div className="song lyrics-only">
        <div className="lyrics-only-wrapper">
          <div className="lyrics-only-text">{lyricsOnlyText}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="song">
      <div className={`lines columns-1`}>
        {song.sections.map((section, index) => (
          <SectionElements
            key={index}
            section={section}
            songCurrentPosition={song.currentPosition}
            areChordsVisible={areChordsVisible}
            isLyricSeparatorEnabled={isLyricSeparatorEnabled}
            isLyricsOnlyMode={isLyricsOnlyMode}
          />
        ))}
      </div>
    </div>
  );
};

export default PresentationSongComponent;
